import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FaListUl, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { MultiSelect } from 'src/components/Form/multi-select';
import { countryOptions } from 'src/constants/countries';
import { CreateRewardFormData } from '../../utils/reward.validation';

const categoryOptions = [
  { id: 'DIGITAL', label: 'Digital' },
  { id: 'PHISICAL', label: 'Physical' }, 
  { id: 'VOUCHER', label: 'Voucher' },
];

export const RewardBasicInfoFields: React.FC = () => {
  const { register, control, watch, formState: { errors, touchedFields } } = useFormContext<CreateRewardFormData>();
  const providerName = watch('providerName');
  const isTremendousReward = providerName === 'TREMENDOUS';

  const getFieldBorderColor = (field: keyof CreateRewardFormData) => {
    if (errors[field]) return 'border-red-300 focus:ring-red-500 focus:border-red-500';
    if (touchedFields[field]) return 'border-green-300 focus:ring-green-500 focus:border-green-500';
    return 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500';
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100 space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Step 2</p>
          <h3 className="mt-2 text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaListUl className="text-indigo-500" /> Basic Information
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Add the reward details your users will see after the provider setup is ready.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">Reward Name *</label>
            <input
              type="text"
              {...register('name')}
              className={`block w-full px-4 py-3 border-2 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-offset-2 text-md font-medium transition-all duration-500 hover:shadow-lg hover:from-gray-50 hover:to-white transform hover:scale-[1.02] ${getFieldBorderColor('name')}`}
              placeholder="e.g. $10 Amazon Gift Card"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><FaExclamationTriangle /> {errors.name.message}</p>}
            {!errors.name && touchedFields.name && <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><FaCheckCircle /> Looks good!</p>}
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">Karma Required *</label>
            <input
              type="number"
              min="1"
              {...register('amount')}
              className={`block w-full px-4 py-3 border-2 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-offset-2 text-md font-mono transition-all duration-500 hover:shadow-lg hover:from-gray-50 hover:to-white transform hover:scale-[1.02] ${getFieldBorderColor('amount')}`}
            />
            {errors.amount && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><FaExclamationTriangle /> {errors.amount.message}</p>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">Category</label>
            {isTremendousReward ? (
              <div className="block w-full px-4 py-3 border-2 border-fuchsia-200 rounded-2xl leading-5 bg-fuchsia-50 text-md font-medium text-fuchsia-700">
                Digital
              </div>
            ) : (
              <select
                {...register('category')}
                className="block w-full px-4 py-3 border-2 border-gray-300 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:border-indigo-500 focus:ring-offset-2 text-md font-medium transition-all duration-500 hover:shadow-lg transform hover:scale-[1.02] cursor-pointer"
              >
                {categoryOptions.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            )}
            {isTremendousReward && (
              <p className="text-xs text-fuchsia-600">Tremendous rewards are locked to the digital category.</p>
            )}
          </div>

          <div className="md:col-span-2 space-y-2 relative group z-10">
            <Controller
              name="countryList"
              control={control}
              render={({ field }) => (
                <MultiSelect 
                  label="Available Countries" 
                  options={countryOptions}
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  placeholder={isTremendousReward ? "Selected Tremendous-supported countries" : "Select countries (leave blank for global)"}
                  maxVisibleBadges={isTremendousReward ? 3 : undefined}
                />
              )}
            />
            {errors.countryList && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <FaExclamationTriangle /> {errors.countryList.message}
              </p>
            )}
            <p className="text-xs text-gray-400">
              {isTremendousReward
                ? 'Countries are prefilled from the selected Tremendous product. You can narrow the list if needed.'
                : 'Select applicable countries.'}
            </p>
          </div>

          <div className="md:col-span-2 space-y-2">
            <label className="text-sm font-semibold text-gray-700">Expiration Time (Optional)</label>
            <input
              type="datetime-local"
              {...register('expiresAt')}
              className="block w-full px-4 py-3 border-2 border-gray-300 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:border-indigo-500 focus:ring-offset-2 text-md transition-all duration-500 hover:shadow-lg transform hover:scale-[1.02]"
            />
            <p className="text-xs text-gray-400">Leave blank if the reward never expires.</p>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-sm font-semibold text-gray-700">Description *</label>
          <textarea
            {...register('description')}
            rows={4}
            className={`block w-full px-4 py-3 border-2 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-offset-2 text-md font-medium transition-all duration-500 hover:shadow-lg hover:from-gray-50 hover:to-white transform hover:scale-[1.02] resize-none ${getFieldBorderColor('description')}`}
            placeholder="Describe the reward..."
          />
          {errors.description && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><FaExclamationTriangle /> {errors.description.message}</p>}
        </div>

        <div className="flex items-center gap-3 pt-2 pb-4 border-b border-gray-100">
          <input
            type="checkbox"
            id="isAvailable"
            {...register('isAvailable')}
            className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 border focus:border-indigo-500"
          />
          <label htmlFor="isAvailable" className="font-medium text-gray-800">
            Make this reward immediately available to users
          </label>
        </div>
      </div>
    </div>
  );
};
